"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, Mail, MapPin } from "lucide-react";
import { ContactSection } from "@/components/landing/contact-form";

export default function ContactPage() {
  const [isLightTheme, setIsLightTheme] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "light") {
      setIsLightTheme(true);
      document.documentElement.classList.add("light");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isLightTheme;
    setIsLightTheme(newTheme);
    localStorage.setItem("theme", newTheme ? "light" : "dark");
    if (newTheme) {
      document.documentElement.classList.add("light");
    } else {
      document.documentElement.classList.remove("light");
    }
  };

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/contact", label: "Contact" },
    { href: "/terms", label: "Terms" },
    { href: "/privacy", label: "Privacy" },
    { href: "/cookies", label: "Cookies" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col landing-theme">
      {/* ============================================ */}
      {/* NAVIGATION - MATCHING LANDING PAGE */}
      {/* ============================================ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/30 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - proportional sizing */}
            <Link href="/" className="flex items-center">
              <span className="text-xl portrait:text-[clamp(1.25rem,2.5vw,1.75rem)] font-bold text-foreground tracking-tight">Lateral</span>
            </Link>

            {/* Separator - Desktop only (lg+) */}
            <div className="hidden lg:block separator-line mx-6" />

            {/* Menu Items - Desktop only (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-6 portrait:gap-[clamp(1.5rem,2vw,2rem)] flex-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm portrait:text-[clamp(0.875rem,1.2vw,1rem)] text-foreground-secondary hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Auth Buttons + Theme Toggle - Desktop (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-3 portrait:gap-[clamp(0.75rem,1.2vw,1rem)]">
              {/* Theme Toggle - Desktop */}
              <button
                onClick={toggleTheme}
                className="ml-2 p-2 portrait:p-[clamp(0.5rem,0.8vw,0.75rem)] rounded-lg text-foreground-secondary hover:text-foreground hover:bg-white/5 transition-all"
                aria-label="Toggle theme"
              >
                {isLightTheme ? (
                  <Moon className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" />
                ) : (
                  <Sun className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" />
                )}
              </button>
            </div>

            {/* Mobile/Tablet: Theme Toggle + Hamburger (below lg) */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Theme Toggle - Mobile */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-foreground-secondary hover:text-foreground transition-colors"
                aria-label="Toggle theme"
              >
                {isLightTheme ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-foreground hover:text-foreground-secondary transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-md border-b border-border/30 shadow-lg"
            >
              <div className="py-6 flex flex-col items-center gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-base text-foreground-secondary hover:text-foreground transition-colors py-2"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ============================================ */}
      {/* MAIN CONTENT - NARROWER WIDTH */}
      {/* ============================================ */}
      <main className="flex-1 pt-[73px]">
        <div className="max-w-4xl mx-auto px-6 py-12 md:py-16 lg:py-20">
          {/* Page Title */}
          <div className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Contact us
            </h1>
            <p className="text-lg text-foreground-secondary mb-8">
              Get in touch with the Lateral Teams team
            </p>
            <div className="h-px bg-foreground/10" />
          </div>

          {/* Contact Cards - 2 column grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Email */}
            <div className="bg-foreground/5 border border-foreground/10 rounded-xl p-6 hover:border-foreground/20 transition-colors">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-accent/10 shrink-0">
                  <Mail className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Email</h3>
                  <a
                    href="mailto:info@lateralteams.com"
                    className="text-accent hover:underline block break-all"
                  >
                    info@lateralteams.com
                  </a>
                  <p className="text-foreground-secondary text-sm mt-2">
                    We typically respond within 24 hours
                  </p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="bg-foreground/5 border border-foreground/10 rounded-xl p-6 hover:border-foreground/20 transition-colors">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-accent/10 shrink-0">
                  <MapPin className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Office address</h3>
                  <address className="not-italic text-foreground-secondary">
                    Lateral LTD<br />
                    20-22 Wenlock Road<br />
                    London, UK
                  </address>
                </div>
              </div>
            </div>
          </div>

          {/* Support Hours */}
          <div className="mb-12 pb-12 border-b border-foreground/10">
            <h3 className="text-xl font-semibold text-foreground mb-3">Support hours</h3>
            <p className="text-foreground-secondary leading-relaxed">
              Our team is available Monday through Friday, 9:00 AM to 6:00 PM GMT. We strive to respond to all inquiries within one business day.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6">Quick links</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <Link
                href="/terms"
                className="p-5 bg-foreground/5 border border-foreground/10 rounded-xl hover:border-accent/50 hover:bg-foreground/10 transition-all group"
              >
                <h3 className="font-semibold text-foreground mb-1 group-hover:text-accent transition-colors">Terms & conditions</h3>
                <p className="text-foreground-secondary text-sm">
                  Read our terms of service and usage policies
                </p>
              </Link>

              <Link
                href="/privacy"
                className="p-5 bg-foreground/5 border border-foreground/10 rounded-xl hover:border-accent/50 hover:bg-foreground/10 transition-all group"
              >
                <h3 className="font-semibold text-foreground mb-1 group-hover:text-accent transition-colors">Privacy policy</h3>
                <p className="text-foreground-secondary text-sm">
                  Learn how we protect and handle your data
                </p>
              </Link>

              <Link
                href="/cookies"
                className="p-5 bg-foreground/5 border border-foreground/10 rounded-xl hover:border-accent/50 hover:bg-foreground/10 transition-all group"
              >
                <h3 className="font-semibold text-foreground mb-1 group-hover:text-accent transition-colors">Cookie policy</h3>
                <p className="text-foreground-secondary text-sm">
                  Understand how we use cookies and tracking
                </p>
              </Link>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-4xl mx-auto px-6">
          <ContactSection />
        </div>
      </main>

      {/* ============================================ */}
      {/* FOOTER - MATCHING LANDING PAGE */}
      {/* ============================================ */}
      <footer className="relative border-t border-border pt-6 pb-0 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back to Top */}
          <div className="flex justify-end mb-6">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-sm text-foreground-secondary hover:text-foreground transition-colors flex items-center gap-1"
            >
              Back to top <span className="text-base">↑</span>
            </a>
          </div>

          {/* Footer content - responsive structure */}
          {/* Mobile & Tablet (< lg): Centered layout */}
          <div className="lg:hidden flex flex-col items-center gap-6 pb-6">
            <Link href="/" className="flex items-center">
              <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
            </Link>
            
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-foreground-secondary">
              <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
              <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm text-foreground-secondary">
                20-22 Wenlock Road, London, UK
              </p>
              <p className="text-sm text-foreground-secondary">
                info@lateralteams.com
              </p>
            </div>
            <p className="text-sm text-foreground-muted text-center">
              © 2026 Lateral Teams · All rights reserved
            </p>
          </div>

          {/* Desktop (lg+): Multi-row layout */}
          <div className="hidden lg:block pb-6">
            {/* Top row: Logo + Main Nav */}
            <div className="flex items-center gap-8 mb-6">
              <Link href="/" className="flex items-center shrink-0">
                <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
              </Link>
            </div>
            {/* Bottom row: Legal + Company Info + Copyright */}
            <div className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-5 text-sm text-foreground-secondary">
                <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
                <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
                <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
                <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
              </div>
              <div className="flex items-center gap-6 text-sm text-foreground-secondary">
                <span>20-22 Wenlock Road, London, UK</span>
                <span>·</span>
                <span>info@lateralteams.com</span>
              </div>
              <p className="text-sm text-foreground-muted shrink-0">
                © 2026 Lateral Teams · All rights reserved
              </p>
            </div>
          </div>
        </div>

        {/* Large Lateral text - 60% visible, 40% hidden - consistent on all screens */}
        <div className="relative h-[14vw] mt-4 overflow-hidden">
          <span
            className="absolute left-1/2 -translate-x-1/2 bottom-[-55%] text-[23vw] font-bold leading-none tracking-[0.02em] select-none pointer-events-none whitespace-nowrap"
            style={{ color: "var(--watermark-color)" }}
          >
            Lateral
          </span>
        </div>
      </footer>
    </div>
  );
}
